import { Box, Stack } from "@mui/material";
import { Input, Text } from "@src/appV2/redesign/system/ui-components";
import { useRef } from "react";
import { type Control, Controller, type FormState } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";

import { useScrollToViewOnKeyboard } from "../../utils/useScrollToViewOnKeyboard";
import { type AddLicenseFormData } from "./FormProvider";
import { type AddLicenseFormFieldBehavior } from "./useFormFieldBehavior";

interface AddLicenseFormProps {
  qualificationId: string;
  addQualificationPath: string;
  addStatePath: string;
  formContext: {
    selectedState?: string;
    fieldBehavior?: AddLicenseFormFieldBehavior;
    control: Control<AddLicenseFormData>;
    formState: Pick<FormState<AddLicenseFormData>, "errors">;
  };
}

export function AddLicenseForm(props: AddLicenseFormProps) {
  const { qualificationId, addQualificationPath, addStatePath, formContext } = props;

  const licenseInputRef = useRef<HTMLInputElement>(null);
  useScrollToViewOnKeyboard(licenseInputRef);

  const {
    selectedState,
    control,
    fieldBehavior,
    formState: { errors },
  } = formContext;

  const history = useHistory();

  return (
    <Box sx={{ paddingX: 6 }}>
      <Stack spacing={7}>
        <Stack spacing={1}>
          <Controller
            name="qualificationName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                readOnly
                size="medium"
                placeholder="Qualification"
                error={Boolean(errors.qualificationName)}
                onKeyDown={(event) => {
                  event.preventDefault();
                }}
                onPaste={(event) => {
                  event.preventDefault();
                }}
                onChange={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  history.replace(generatePath(addQualificationPath, { qualificationId }));
                }}
              />
            )}
          />
          {errors.qualificationName && (
            <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
              {errors.qualificationName.message}
            </Text>
          )}
        </Stack>

        {!fieldBehavior?.state.hidden && (
          <Stack spacing={1}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  readOnly
                  size="medium"
                  placeholder="State"
                  value={selectedState}
                  error={Boolean(errors.state)}
                  onKeyDown={(event) => {
                    event.preventDefault();
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                  }}
                  onChange={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    history.replace(generatePath(addStatePath, { qualificationId }));
                  }}
                />
              )}
            />
            {errors.state && (
              <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
                {errors.state.message}
              </Text>
            )}
          </Stack>
        )}

        {!fieldBehavior?.licenseNumber.hidden && (
          <Stack spacing={1}>
            <Controller
              name="licenseNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  ref={licenseInputRef}
                  size="medium"
                  placeholder="License Number"
                  error={Boolean(errors.licenseNumber)}
                />
              )}
            />
            {errors.licenseNumber && (
              <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
                {errors.licenseNumber.message}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
